import { AiThinkingIcon } from "./aiThinkingIcon";
import clsx from "clsx";
import { useEffect, useRef, useContext, useState, useMemo } from "react";
import AGIWorkflowCard from "./agiWorkflowCard";
import { SignalContext } from "context/SignalContext";
import {
  subAlerts,
  subAlertWorkflow,
  unsubAlerts,
  unsubAlertWorkflow,
} from "../service";
import { AlertDataType } from "lib/types";
import { showToast } from "ui/components/common";
import type { AGIFlowType } from "./agiWorkflowCard";
import { motion } from "motion/react";

export default function AGIWordflow({
  onSelectAlert,
  selectedAlert,
  onInvestigated,
}: {
  onSelectAlert: (alert: AlertDataType) => void;
  selectedAlert: AlertDataType;
  onInvestigated?: (alert: AlertDataType) => void;
}) {
  const [wordflowData, setWordflowData] = useState<AGIFlowType[]>([]);
  const [showThinkingIcon, setShowThinkingIcon] = useState(false);
  const statusRef = useRef(0); // 0: init, 1: Perception, 2: Recognition, 3: Decision, 4: Action, 5: share,  6: End
  const { sessionId, wsMsgData, wsIsOpen } = useContext(SignalContext);
  const workflowDivRef = useRef<HTMLDivElement>();
  const earliestUpdateTimeRef = useRef(0);
  const delayTimeRef = useRef(0);

  // 4min re subscribe
  useEffect(() => {
    if (!sessionId || !wsIsOpen) {
      return;
    }
    const timer = setInterval(
      () => {
        subAlerts(sessionId);
      },
      4 * 60 * 1000,
    );
    //
    subAlerts(sessionId);
    return () => {
      clearInterval(timer);
      if (sessionId) {
        unsubAlerts(sessionId);
      }
    };
  }, [sessionId, wsIsOpen]);

  useEffect(() => {
    if (!sessionId || !selectedAlert || !wsIsOpen) {
      return;
    }
    const timer = setInterval(
      () => {
        subAlertWorkflow(selectedAlert?.id, sessionId);
      },
      4 * 60 * 1000,
    );
    subAlertWorkflow(selectedAlert?.id, sessionId);
    return () => {
      clearInterval(timer);
      if (selectedAlert) {
        console.log("unsubAlertWorkflow", selectedAlert?.id, sessionId);
        unsubAlertWorkflow(selectedAlert?.id, sessionId);
      }
    };
  }, [selectedAlert, wsIsOpen]);

  // useEffect(() => {
  //   const scrollToBottom = () => {
  //     if (workflowDivRef.current) {
  //       workflowDivRef.current.scrollTop = workflowDivRef.current.scrollHeight;
  //     }
  //   };
  //   setTimeout(() => scrollToBottom(), 800);
  // }, [wordflowData]);

  useEffect(() => {
    //sidebar camera status change
    if (
      !wsMsgData ||
      !wsMsgData.result ||
      wsMsgData.result.act !== "alert.workflow.created"
    ) {
      return;
    }
    if (statusRef.current !== 6 && statusRef.current !== 0) {
      return;
    }

    setWordflowData([]);
    setShowThinkingIcon(true);

    const data = wsMsgData.result.data?.alertWorkflowCreated?.alert;
    onSelectAlert(data);
    const perceptionData: AGIFlowType = {
      type: "Perception",
      alertTime: +data?.createdAt,
      alertType: data?.eventTypes?.at(0) || "-",
      location: data?.location?.address,
    };
    statusRef.current = 1;
    // Make sure to clear wordflowData first
    setTimeout(() => {
      updateWorkflow(perceptionData);
    }, 200);
  }, [wsMsgData]);

  const updateWorkflow = (data: AGIFlowType) => {
    setWordflowData((prev) => [...prev, data]);
  };

  useEffect(() => {
    if (
      !wsMsgData ||
      !wsMsgData.result ||
      wsMsgData.result.act !== "workflow.updated"
    ) {
      return;
    }
    console.log(
      "workflow.updated",
      wsMsgData.result.data?.workflowUpdated?.workflow?.id,
      statusRef.current,
    );
    if (statusRef.current === 0 || statusRef.current === 6) {
      return;
    }

    //let delayTime = 0;

    if (statusRef.current === 1) {
      const recognitionData =
        wsMsgData.result.data?.workflowUpdated?.workflow?.actions?.find(
          (action) => action.type === "describe_alert",
        );

      if (recognitionData && recognitionData.status === "success") {
        const workflowData: AGIFlowType = {
          type: "Recognition",
          alertTime: +recognitionData.updatedAt,
          tag: wsMsgData.result.data?.workflowUpdated?.data
            ?.workflow_alert_tags,
          alertImgUrl: selectedAlert?.snapshot?.url,
        };

        updateWorkflow(workflowData);
        statusRef.current = 2;
      }
    }
    if (statusRef.current === 2) {
      const decisionData =
        wsMsgData.result.data?.workflowUpdated?.workflow?.actions?.find(
          (action) => action.type === "decision",
        );

      if (decisionData && decisionData.status === "success") {
        const suggestedChoice = decisionData?.userSelection?.choices?.find(
          (choice) => choice.isSuggested,
        );
        const otherChoicesTypes = decisionData?.userSelection?.choices?.filter(
          (choice) => !choice.isSuggested,
        );
        const workflowData: AGIFlowType = {
          type: "Decision",
          alertTime: +decisionData.updatedAt,
          recommendedChoice: suggestedChoice,
          reason:
            wsMsgData.result.data?.workflowUpdated?.data?.workflow_alert_reason,
          otherChoices: otherChoicesTypes,
          countdown: decisionData?.userSelection?.countdown,
          countdownStart: decisionData?.userSelection?.countdownStart,
        };

        updateWorkflow(workflowData);
        statusRef.current = 3;
      }
    }
    if (statusRef.current === 3) {
      const actions = wsMsgData.result.data?.workflowUpdated?.workflow?.actions;

      const actionData =
        actions?.find((action) => action.type === "close_alert") ||
        actions?.find((action) => action.type === "false_alarm_alert") ||
        actions?.find((action) => action.type === "investigate_alert");
      if (actionData && actionData.status === "success") {
        const workflowData: AGIFlowType = {
          type: "Action",
          alertTime: +actionData.updatedAt,
          actionText: actionData.type,
        };

        updateWorkflow(workflowData);
        statusRef.current = 4;
      }
    }

    if (statusRef.current === 4) {
      const actions = wsMsgData.result.data?.workflowUpdated.workflow.actions;
      const actionData = actions?.find(
        (action) => action.type === "share_alert_to_admin",
      );
      if (actionData && actionData.status === "success") {
        onInvestigated(selectedAlert);
        const workflowData: AGIFlowType = {
          type: "Action",
          alertTime: +actionData.updatedAt,
          actionText: actionData.type,
          sendTo:
            wsMsgData.result.data?.workflowUpdated.data?.share_to_user
              ?.firstName +
            " " +
            wsMsgData.result.data?.workflowUpdated.data?.share_to_user
              ?.lastName,
        };
        updateWorkflow(workflowData);
        statusRef.current = 5;
      }
    }

    // end workflow
    if (
      showThinkingIcon &&
      (wsMsgData.result.data?.workflowUpdated.workflow.status === "success" ||
        wsMsgData.result.data?.workflowUpdated.workflow.status === "failed")
    ) {
      setShowThinkingIcon(false);
      setTimeout(() => {
        statusRef.current = 6;
      }, 5000);
    }
  }, [wsMsgData]);

  return (
    <motion.div
      className={clsx(
        "absolute top-4 left-4 z-[1]  bg-white rounded-2xl w-[380px] shadow-lg",
        wordflowData.length > 0 ? "h-auto py-4" : "h-0",
      )}
    >
      <div
        style={{
          maxHeight: "calc(100vh - 250px)",
        }}
        ref={workflowDivRef}
        className="min-h-10 z-10 px-4 break-words overflow-auto"
      >
        {wordflowData.map((item, index) => (
          <AGIWorkflowCard key={index} data={item} />
        ))}
      </div>
      {showThinkingIcon && (
        <div
          style={{
            placeItems: "center",
            padding: "1rem 0px 0px 0px",
          }}
        >
          <AiThinkingIcon />
        </div>
      )}
    </motion.div>
  );
}
